import React, { useState, useEffect } from "react";
import { StepProps } from "../common/types";
import { useAuth } from "../../context/AuthContext";
import { getAllDesignations } from "../../api/master";
import Select from "react-select";
import { fetchUplineManager, getAllEmployees } from "../../api/employee";
import EmployeeMain from "../../types/employee";

type DesignationType = {
  id: string | number;
  tenant_id: string | number;
  rank: number;
  name: string;
  created_at: string;
  updated_at: string;
};

export const WorkInfoTab: React.FC<StepProps> = ({ formik, currentEmployee }) => {
  const { user } = useAuth();
  const [designations, setDesignations] = useState<DesignationType[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<DesignationType | null>(null);
  const [users, setUsers] = useState<EmployeeMain[]>([]);
  const [assignedToList, setAssignedToList] = useState<EmployeeMain[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [designationsRes, usersRes] = await Promise.all([
          getAllDesignations(user.tenant_id),
          getAllEmployees(user.tenant_id)
        ]);

        setDesignations(designationsRes);
        setUsers(usersRes);

        // If editing, set the initial designation
        if (currentEmployee?.designation_id && designationsRes.length > 0) {
          const designation = designationsRes.find(
            (d: DesignationType) => Number(d.id) === Number(currentEmployee.designation_id)
          );
          if (designation) {
            setSelectedDesignation(designation);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user.tenant_id, currentEmployee]);

  // Fetch upline managers when designation changes
  useEffect(() => {
    const fetchUplineManagers = async () => {
      if (!selectedDesignation?.id) {
        setAssignedToList([]);
        return;
      }

      try {
        const response = await fetchUplineManager(
          user.tenant_id,
          Number(selectedDesignation.id),
          selectedDesignation.rank
        );
        setAssignedToList(response || []);
      } catch (err) {
        console.log("Error fetching upline managers: ", err);
        setAssignedToList([]);
      }
    };

    fetchUplineManagers();
  }, [selectedDesignation, user.tenant_id]);

  // const selectedAccompanied = users
  //   .filter(user =>
  //     Array.isArray(formik.values.accompanied_employee) &&
  //     formik.values.accompanied_employee.includes(Number(user.id))
  //   )
  //   .map(user => ({
  //     value: String(user.id),
  //     label: user.user?.name || `Employee ${user.id}`,
  //   }));

  // Filter out assigned_to from additional supervisor options
  const filteredSupervisors = assignedToList.filter(
    (u) =>
      Number(u.id) !== Number(formik.values.assigned_to) ||
      Number(u.id) === Number(formik.values.additional_supervisor)
  );

  // // Filter out additional supervisor from accompanied employee options
  // const filteredAccompanied = users.filter(
  //   (u) => Number(u.id) !== Number(formik.values.additional_supervisor)
  // );

  // const accompaniedOptions = filteredAccompanied.map((u) => ({
  //   value: String(u.id),
  //   label: u.user?.name || `Employee ${u.id}`,
  // }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-b-2 border-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="pb-2 text-lg font-semibold text-gray-900 border-b border-gray-200">
        Work Information
      </h3>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Select Designation *
          </label>
          <select
            name="designation_id"
            value={formik.values.designation_id || ""}
            onChange={(e) => {
              const selectedId = Number(e.target.value);
              formik.setFieldValue("designation_id", selectedId);

              const designation = designations.find(
                (d) => Number(d.id) === selectedId
              );
              setSelectedDesignation(designation || null);

              // Reset assigned_to when designation changes
              formik.setFieldValue("assigned_to", 0);
            }}
            onBlur={formik.handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Designation</option>
            {designations.map((designation) => (
              <option key={designation.id} value={designation.id}>
                {designation.name}
              </option>
            ))}
          </select>
          {formik.touched.designation_id && formik.errors.designation_id && (
            <div className="mt-1 text-sm text-red-600">{formik.errors.designation_id}</div>
          )}
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Assigned To
          </label>
          <select
            name="assigned_to"
            value={formik.values.assigned_to || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={!selectedDesignation}
          >
            <option value="">Select Assigned To</option>
            {assignedToList.map((emp) => (
              <option key={emp.id} value={emp.user_id}>
                {emp.user?.name + ' ' + emp.designation?.name || `Employee ${emp.user_id}`}
              </option>
            ))}
          </select>

          {selectedDesignation && assignedToList.length === 0 && (
            <div className="mt-1 text-sm text-yellow-600">
              No available managers for this designation
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Additional Supervisor
          </label>
          <select
            name="additional_supervisor"
            value={formik.values.additional_supervisor || ""}
            onChange={(e) => {
              const value = e.target.value === "" ? 0 : Number(e.target.value);
              formik.setFieldValue("additional_supervisor", value);
            }}
            onBlur={formik.handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Additional Supervisor</option>
            {filteredSupervisors.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.user?.name || `Employee ${emp.id}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Date Of Joining *
          </label>
          <input
            type="date"
            name="doj"
            value={formik.values.doj || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          {formik.touched.doj && formik.errors.doj && (
            <div className="mt-1 text-sm text-red-600">{formik.errors.doj}</div>
          )}
        </div>
      </div>

      {/* Accompanied Employee (disabled)
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Accompanied Employee
        </label>
        <Select
          isMulti
          name="accompanied_employee"
          options={accompaniedOptions}
          value={selectedAccompanied}
          onChange={(selected) => {
            const ids = selected ? selected.map((s: any) => Number(s.value)) : [];
            formik.setFieldValue("accompanied_employee", ids);
          }}
          onBlur={() => formik.setFieldTouched("accompanied_employee", true)}
          className="basic-multi-select"
          classNamePrefix="select"
          placeholder="Select accompanied employees..."
        />
        {formik.touched.accompanied_employee && formik.errors.accompanied_employee && (
          <div className="mt-1 text-sm text-red-600">{formik.errors.accompanied_employee}</div>
        )}
      </div>
      */}
    </div>
  );
};